/**
 * Lightweight markdown renderer for chat bubbles and other rich text.
 * Wraps react-markdown (GFM) with compact Tailwind styling that inherits the
 * surrounding bubble's colour and font size, so agent replies can use bold,
 * lists, links and headings without leaking raw `**` / `-` syntax.
 */
import { memo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { toDirhamSymbol } from '@servicesai/shared';

export interface MarkdownProps {
  children: string;
  className?: string;
}

/* `remarkBreaks` because a single newline is a line break here and not a
 * space. Markdown's own rule — fold a lone newline into the paragraph — is
 * for prose somebody typed into a file with a fill column. A reply is
 * assembled from lines the engine meant to keep apart: `submitted\nreference`
 * rendered as one run, so the reference number ran into the sentence above
 * it, and the concierge bubble has no `whitespace-pre-wrap` to fall back on.
 */
const PLUGINS = [remarkGfm, remarkBreaks];

/* Built once, at module scope, because react-markdown treats a new `components`
 * object as a reason to rebuild the tree — and this was being rebuilt for every
 * message in the transcript on every keystroke in the composer. */
const COMPONENTS: Components = {
  p: ({ children }) => <p className="my-1.5 first:mt-0 last:mb-0">{children}</p>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="font-semibold text-oasis underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="my-1.5 list-disc space-y-1 ps-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 list-decimal space-y-1 ps-5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="md-strong">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => <h1 className="mt-2 mb-1 text-base font-bold first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="mt-2 mb-1 text-sm font-bold first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-2 mb-1 text-sm font-semibold first:mt-0">{children}</h3>,
  blockquote: ({ children }) => (
    <blockquote className="my-1.5 border-s-2 border-ink/20 ps-3 text-ink-soft italic">{children}</blockquote>
  ),
  code: ({ className: cls, children }) => {
    const isBlock = /language-/.test(cls ?? '');
    if (isBlock) {
      return (
        <code className="block overflow-x-auto rounded-lg bg-ink/10 px-3 py-2 font-mono text-[0.85em]">
          {children}
        </code>
      );
    }
    return <code className="rounded bg-ink/10 px-1 py-0.5 font-mono text-[0.85em]">{children}</code>;
  },
  pre: ({ children }) => <pre className="my-1.5">{children}</pre>,
  hr: () => <hr className="my-2 border-ink/10" />,
  table: ({ children }) => (
    <div className="my-1.5 overflow-x-auto">
      <table className="w-full border-collapse text-[0.9em]">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-ink/15 px-2 py-1 text-start font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-ink/15 px-2 py-1">{children}</td>,
};

/**
 * Memoised on its text, because parsing it is the most expensive thing the
 * transcript does and the transcript re-renders for reasons that have nothing
 * to do with any given reply — a token arriving in the newest one, a character
 * typed in the composer. Every older message was being re-parsed each time.
 */
export const Markdown = memo(function Markdown({ children, className }: MarkdownProps) {
  /* Every agent reply on every web surface arrives here, which makes this the
   * one place a fee written as "AED 150" becomes the dirham sign. The engine
   * keeps producing the canonical wording — it is what the grounding check and
   * the guardrails read, and what text-to-speech has to pronounce — so the
   * substitution belongs at the render seam and nowhere earlier. */
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={PLUGINS} components={COMPONENTS}>
        {toDirhamSymbol(children)}
      </ReactMarkdown>
    </div>
  );
});
